/**
 * CME session phases — so a closed market stops being reported as a broken feed.
 *
 * ⚠ THIS CALENDAR IS ES-ONLY. IT DESCRIBES CME EQUITY-INDEX FUTURES AND NOTHING ELSE.
 * Applying it to another instrument produces confident, wrong answers. Measured, live, in our
 * own spine — VIX settles 16:15 ET (20:15Z) while ES trades on to 23:59Z, three days running:
 *     VIX   08-04 last 20:10 · 08-05 last 20:15 · 08-06 last 20:15
 *     ES1!  08-04 last 23:59 · 08-05 last 23:59 · 08-06 last 23:43
 * Run this module against VIX at 20:20Z / 22:30Z / 02:00Z and it reports "session open" and the
 * chip reads STALE — for an instrument that closed on schedule hours earlier. A BUILDER seat
 * filed exactly that as a staleness fault twice before anyone noticed the calendar was the bug.
 *
 * CC ruling R-3 (@197da9d) settles the shape: the session is a property of the INSTRUMENT, not
 * of the platform — "SPX is not SPXW because root belongs to the ROW; ES is not VIX because the
 * session belongs to the INSTRUMENT". A single platform-wide "is the market open" is law 2's
 * pooling error one level up. R-3 also rules this verdict belongs SERVER-SIDE, because the alarm
 * and every non-browser consumer will never load this file. So: do NOT port this module as it
 * stands, and do NOT reuse it for a second instrument — it needs (instant, instrument), and
 * calendars sourced from cadence rather than hardcoded here.
 *
 * THE DEFECT THIS EXISTS TO FIX. The staleness chip has one budget (90s) and one failure state
 * (STALE). CME halts for an hour every weekday and closes for two days every weekend, so the chip
 * goes red daily for an hour and all weekend — and BUILDER measured exactly how much, over 30,000
 * consecutive live ES1! bars (wiki/data/ES_FEAT_DICTIONARY_v1.md §4):
 *
 *     gap        count   what it is
 *     60 s       29,947  normal cadence
 *     120 s          25  one dropped bar (0.08%)
 *     3,660 s        17  CME daily maintenance halt — structural, EVERY session
 *     176,460 s       4  weekend close (49 h)       — structural, EVERY week
 *
 * A signal that cries wolf for an hour a day and all weekend is one people learn to ignore, and a
 * fail-loud chip nobody reads is worse than no chip: it is fail-loud theatre. The answer is NOT to
 * widen the budget until it goes quiet — that would hide a genuine 61-minute outage. It is a THIRD,
 * HONEST STATE: the market is closed, we know it, and this is not a fault.
 *
 * TWO THINGS THE MEASUREMENTS FORCED INTO THE DESIGN:
 *
 * 1. THE HALT IS 61 MINUTES, NOT 60 — 3,660s, seventeen times, no variance. A rule written against a
 *    clean 16:00–17:00 window still breaches by a minute at the reopen edge every single day, which
 *    is the once-a-day false alarm we are trying to remove. Hence REOPENING: a grace window after the
 *    session resumes, during which the first bar has not arrived yet and that is expected, not late.
 *
 * 2. CLOSED MUST NEVER HIDE A REAL OUTAGE. Being inside a closed window is not sufficient on its own:
 *    if the feed died at noon and the market later closed, the data is still missing and the panel
 *    must still say STALE. So CLOSED additionally requires the last bar to be CONSISTENT with the
 *    close — printed at or after the moment trading stopped. A closed window excuses the silence
 *    that follows it, never the silence before it.
 *
 * Times are computed in America/Chicago via Intl, not as a hardcoded UTC offset, so the US DST
 * transition moves the session with the exchange instead of shifting it an hour twice a year.
 */

export type SessionPhase = "open" | "halt" | "weekend" | "reopening";

export interface SessionState {
  phase: SessionPhase;
  /** True when the market itself is not trading — a fact about the world, not about our pipeline. */
  marketClosed: boolean;
  /** Epoch ms at which the current closed period began. null while open. */
  closedSince: number | null;
  /** Epoch ms at which trading resumes. null while open. */
  reopensAt: number | null;
  /** Short human label for the chip, e.g. "CME halt" / "weekend". */
  label: string;
}

/** CME equity-index futures, in exchange local time (America/Chicago). */
const HALT_START_MIN = 16 * 60; // 16:00 CT — trading stops
const HALT_END_MIN = 17 * 60; // 17:00 CT — trading resumes
/** The measured halt is 3,660s. A bar due at the reopen is not late until this has passed. */
const REOPEN_GRACE_S = 180;
/** How far before the close a last bar may sit and still be "consistent with" it. */
const CLOSE_CONSISTENCY_GRACE_S = 300;

interface CtParts {
  year: number; month: number; day: number; hour: number; minute: number; dow: number;
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function ctParts(d: Date): CtParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    weekday: "short", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  // Intl renders midnight as "24" in some engines; normalise so 24:xx is 00:xx.
  const hour = Number(get("hour")) % 24;
  return {
    year: Number(get("year")), month: Number(get("month")), day: Number(get("day")),
    hour, minute: Number(get("minute")), dow: Math.max(0, DOW.indexOf(get("weekday"))),
  };
}

/** Minutes that Chicago is offset from UTC at this instant (negative: behind UTC). */
function ctOffsetMinutes(d: Date, p: CtParts): number {
  const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  const flooredNow = Math.floor(d.getTime() / 60_000) * 60_000;
  return (asIfUtc - flooredNow) / 60_000;
}

/** Epoch ms for a wall-clock time in Chicago on the same CT date as `p`, offset by `dayShift`. */
function ctEpoch(p: CtParts, offsetMin: number, minuteOfDay: number, dayShift = 0): number {
  return Date.UTC(p.year, p.month - 1, p.day + dayShift, 0, minuteOfDay) - offsetMin * 60_000;
}

/**
 * Where the CME session is right now. Pure: pass `now` so it can be tested at any instant.
 */
export function sessionAt(now: Date | number = new Date()): SessionState {
  const d = typeof now === "number" ? new Date(now) : now;
  const p = ctParts(d);
  const off = ctOffsetMinutes(d, p);
  const mins = p.hour * 60 + p.minute;

  const open = (): SessionState => ({
    phase: "open", marketClosed: false, closedSince: null, reopensAt: null, label: "session open",
  });

  // Saturday: closed all day. The close began at Friday 16:00 CT.
  if (p.dow === 6) {
    return {
      phase: "weekend", marketClosed: true,
      closedSince: ctEpoch(p, off, HALT_START_MIN, -1),
      reopensAt: ctEpoch(p, off, HALT_END_MIN, 1),
      label: "weekend",
    };
  }
  // Sunday: closed until 17:00 CT, then the week opens.
  if (p.dow === 0) {
    if (mins < HALT_END_MIN) {
      return {
        phase: "weekend", marketClosed: true,
        closedSince: ctEpoch(p, off, HALT_START_MIN, -2),
        reopensAt: ctEpoch(p, off, HALT_END_MIN),
        label: "weekend",
      };
    }
    return withReopenGrace(open(), ctEpoch(p, off, HALT_END_MIN), d);
  }
  // Friday from 16:00 CT: the weekend starts.
  if (p.dow === 5 && mins >= HALT_START_MIN) {
    return {
      phase: "weekend", marketClosed: true,
      closedSince: ctEpoch(p, off, HALT_START_MIN),
      reopensAt: ctEpoch(p, off, HALT_END_MIN, 2),
      label: "weekend",
    };
  }
  // Mon–Fri daily maintenance halt.
  if (mins >= HALT_START_MIN && mins < HALT_END_MIN) {
    return {
      phase: "halt", marketClosed: true,
      closedSince: ctEpoch(p, off, HALT_START_MIN),
      reopensAt: ctEpoch(p, off, HALT_END_MIN),
      label: "CME halt",
    };
  }
  // Open — but if we only just reopened, the first bar is not late yet.
  return withReopenGrace(open(), ctEpoch(p, off, HALT_END_MIN), d);
}

function withReopenGrace(state: SessionState, reopenedAt: number, now: Date): SessionState {
  const since = (now.getTime() - reopenedAt) / 1000;
  if (since >= 0 && since < REOPEN_GRACE_S) {
    return {
      ...state,
      phase: "reopening",
      label: "reopening",
      // The close that just ended. Carried so REOPENING can be held to the SAME consistency test
      // as CLOSED — without it, "the first bar is not due yet" would excuse a feed dead for a day.
      closedSince: reopenedAt - (HALT_END_MIN - HALT_START_MIN) * 60_000,
      reopensAt: reopenedAt,
    };
  }
  return state;
}

export type FeedState = "live" | "stale" | "closed" | "reopening" | "nodata" | "error";

/** Presentation-only memory for the shared staleness chip. The raw FeedState stays authoritative. */
export interface StaleInkState {
  consecutiveOverBudgetPolls: number;
  visible: boolean;
}

/** Additive wire subset shared by every CLOSED-aware panel. */
export interface VenueStateLike {
  venue: string | null;
  verdict: string;
  closure: {
    date: string;
    kind: "FULL" | "EARLY";
    name: string;
    close_local: string | null;
    verified: boolean;
  } | null;
  last_close: string | null;
  next_open: string | null;
}

export interface VenuePanelState {
  closed: boolean;
  pill: string | null;
  alert: boolean;
}

/** One armable decision for every panel face; the browser owns no holiday calendar. */
export function venuePanelState(state: VenueStateLike | null | undefined): VenuePanelState {
  if (state?.verdict === "STALE") return { closed: false, pill: null, alert: true };
  if (state?.verdict !== "CLOSED") return { closed: false, pill: null, alert: false };

  const venue = state.venue?.trim();
  const nextMs = state.next_open === null ? Number.NaN : Date.parse(state.next_open);
  const next = Number.isFinite(nextMs)
    ? new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(new Date(nextMs)).replace(",", "")
    : "unknown";
  return {
    closed: true,
    pill: `CLOSED · ${venue || "UNKNOWN_VENUE"} · next open ${next}${next === "unknown" ? "" : " ET"}`,
    alert: false,
  };
}

/**
 * Damp a single ordinary budget breach without ever damping the underlying verdict.
 * A severe breach is loud immediately; any fresh/non-stale sample clears the ink immediately.
 */
export function advanceStaleInk(
  previous: StaleInkState,
  sample: { rawState: FeedState; ageS: number | null; budgetS: number },
): StaleInkState {
  if (sample.rawState !== "stale") {
    return { consecutiveOverBudgetPolls: 0, visible: false };
  }

  const consecutiveOverBudgetPolls = previous.consecutiveOverBudgetPolls + 1;
  const severeBreach = sample.ageS !== null && sample.ageS > sample.budgetS * 1.5;
  return {
    consecutiveOverBudgetPolls,
    visible: severeBreach || consecutiveOverBudgetPolls >= 2,
  };
}

/**
 * The chip's verdict. This is where CLOSED is prevented from becoming an excuse.
 *
 * `lastBarMs` is the START of the newest bucket we hold. A closed market only explains silence that
 * STARTED at the close: if that bucket ENDED more than the grace before the close, data went
 * missing while the market was trading and the honest answer is still STALE.
 *
 * ⛔⛔ `barSpanMs` IS REQUIRED, AND ITS ABSENCE WAS A LIVE DEFECT ON THE OWNER'S SCREEN.
 * A bar's timestamp is its bucket's START, so at any rung wider than the grace the newest bar
 * necessarily predates the close — and this function, comparing that START, called a perfectly
 * healthy feed a gap. **Three of the five shipped rungs could never return `closed`**: the 1m and
 * 3m panes read `CLOSED · weekend` while the 12m pane read `STALE — this is a gap, not the close`
 * off the SAME spine and the same `seq` (HUDCHAT-4 `8a9fe605`; GK-vv drove the boundary to exactly
 * `CLOSE_CONSISTENCY_GRACE_S`, 1m/3m/5m closed, 6m+ crying gap).
 * ⇒ ***THE GRACE IS A TOLERANCE, AND A TOLERANCE CHOSEN FROM THE DATA'S RESOLUTION IS STILL WRONG
 * AT A BOUNDARY*** — the law this file already states above `SEGMENT_EPSILON_MS`, scoped to the
 * function it was minted in while the rot sat three functions away.
 *
 * ⚠ **NO DEFAULT, DELIBERATELY.** A default would silently hand every future call site the wrong
 * span — and one of the three existing callers (`TickStatePane`) is not a chart rung at all: it
 * passes a TICK, whose honest span is `0`. Requiring the argument forces each site to state what
 * its timestamp actually covers. ⛔ **AND DO NOT "FIX" A FUTURE RECURRENCE BY RAISING THE GRACE:**
 * a grace wide enough for the 1440m rung would let `CLOSED` excuse a day-long outage on the 1m
 * rung, which is the hidden-dead-feed defect this whole branch exists to prevent. `session.test.ts`
 * arms that directly.
 */
export function feedState(opts: {
  ageS: number | null;
  budgetS: number;
  lastBarMs: number | null;
  /** How much time the bar at `lastBarMs` COVERS. Bars: the rung's span. Ticks: `0`. */
  barSpanMs: number;
  error?: string | null;
  now?: Date | number;
  /**
   * ⛔⛔ CLOSED-VENUE-1 box 3 — THE DOOR'S VERDICT, WHICH KNOWS THINGS THIS MODULE CANNOT.
   *
   * `sessionAt()` below is a hardcoded WEEKLY CME rule: Saturday, Sunday-until-17:00-CT, the daily
   * halt. It has no holidays, and it is CME-ONLY — SPX/VIX/SPY/MARKET have no closed concept here
   * at all. That is why US Labor Day rendered as a broken feed on every cash panel: the schedule
   * was right about the hours and blind to the date.
   * ⇒ *** THE APP MUST NOT GROW A HOLIDAY TABLE. *** It would be the THIRD calendar (registry →
   *   venue_closures_v1 → here) and R1582 forbade the second. The hub holds the hours and the
   *   calendar and serves a verdict; this module RENDERS it. Absent (a pre-box-2 server, or a door
   *   that serves none), everything below behaves exactly as it did.
   */
  servedVenueState?: string | null;
}): { state: FeedState; session: SessionState; reason: string } {
  const { ageS, budgetS, lastBarMs, barSpanMs, error = null } = opts;
  const served = opts.servedVenueState ?? null;
  const nowAt = opts.now ?? new Date();
  const nowMs = typeof nowAt === "number" ? nowAt : nowAt.getTime();
  const session = sessionAt(nowAt);

  if (error) return { state: "error", session, reason: error };
  if (ageS === null) return { state: "nodata", session, reason: "no data received yet" };

  const withinBudget = ageS <= budgetS;
  if (withinBudget) return { state: "live", session, reason: "within budget" };

  // ⛔ SERVED **STALE** IS TESTED FIRST, AHEAD OF EVERY LOCAL CLOSED PATH. The door computes it
  // from OPEN ELAPSED TIME with only VERIFIED closures subtracted, so a served STALE means real
  // open time was missed. Letting the local weekly rule's "the market is shut now" excuse it is
  // precisely how a closure hides an outage — the failure this whole object exists to refuse, and
  // the one the block below already refuses on its own terms.
  if (served === "STALE") {
    return {
      state: "stale", session,
      reason: `over budget and the venue calendar says open time elapsed (server verdict STALE)`,
    };
  }
  // A served CLOSED is a CHECKED claim: the door already excluded an outage before saying it, with
  // the calendar this module does not have. It is the FACT; `sessionAt()` stays as the belt below
  // for every door that serves no verdict.
  if (served === "CLOSED") {
    return {
      state: "closed", session,
      reason: `the venue is closed on the calendar (server verdict CLOSED)`,
    };
  }
  // REFUSED is NOT a licence to guess: the door could not judge, so this module judges as it
  // always did, and the reason says the verdict was unavailable rather than favourable.

  if (session.marketClosed && session.closedSince !== null) {
    // CLOSED is a CLAIM — "this silence is explained". A claim we cannot check is not one we make.
    // Without a last-bar timestamp there is no way to tell the close from a feed that died hours
    // earlier, so the verdict stays STALE and says which fact is missing. This is the conservative
    // direction on purpose: a false STALE costs a glance, a false CLOSED hides a dead feed.
    if (lastBarMs === null) {
      return {
        state: "stale", session,
        reason: `over budget during the ${session.label}, but this feed carries no data timestamp — ` +
          `the close cannot be told apart from an outage`,
      };
    }
    // The bucket's END is what sits adjacent to the close; its START is one span earlier and is
    // not a claim about when the data stopped. `barSpanMs` is 0 for tick feeds, so this reduces
    // to the original comparison exactly where the original was right.
    // ⛔⛔ AND IT IS CAPPED BY WHAT WE ACTUALLY OBSERVED — codex review caught this in a draft that
    // had already gone green, and it was a REAL hole. ***A BUCKET'S NOMINAL WIDTH IS NOT EVIDENCE
    // THAT OBSERVATIONS CONTINUED THROUGH IT.*** A 1440m bucket starting Friday 00:00Z has a
    // nominal end of Saturday 00:00Z, so a writer that died at NOON still "reached" the close and
    // rendered a quiet CLOSED, hiding a NINE-HOUR OUTAGE — driven, not argued. `ageS` is the age of
    // the newest DATA (`vendor_ts` / `max(receipt_ts)`, server-side), so `now − ageS` is the last
    // instant we hold evidence for. Crediting the MINIMUM can only move the verdict toward STALE,
    // which is the direction this branch is required to fail in.
    const observedEndMs = nowMs - ageS * 1000;
    const creditedEndMs = Math.min(lastBarMs + barSpanMs, observedEndMs);
    if (creditedEndMs >= session.closedSince - CLOSE_CONSISTENCY_GRACE_S * 1000) {
      return { state: "closed", session, reason: `${session.label} — last print was at the close` };
    }
    // The gap began BEFORE the market shut. A closed window does not explain that.
    return {
      state: "stale", session,
      reason: `data stopped before the ${session.label} began — this is a gap, not the close`,
    };
  }

  if (session.phase === "reopening") {
    // Same narrow door as CLOSED. A grace window explains a bar that has not arrived YET; it does not
    // explain data that stopped long before the halt. Caught by asserting the property rather than
    // trusting the reasoning — the reopening branch used to return unconditionally, so a feed dead
    // for a day went quiet for 180 seconds every single afternoon.
    // ⚠ THE SECOND SITE, AND IT CARRIED THE IDENTICAL DEFECT. The close branch above was the one
    // measured on the owner's screen; this one compares the same bucket START against the same
    // grace, so at 12m+ a healthy feed was denied `reopening` for exactly the same reason.
    // ⇒ ***CURING THE INSTANCE YOU WERE SHOWN IS NOT CURING THE CLASS*** — both floor sites move
    // together or the arm only proves the half someone happened to look at.
    const consistent =
      lastBarMs !== null &&
      session.closedSince !== null &&
      Math.min(lastBarMs + barSpanMs, nowMs - ageS * 1000) >=
        session.closedSince - CLOSE_CONSISTENCY_GRACE_S * 1000;
    if (consistent) {
      return { state: "reopening", session, reason: "session just resumed; first bar not due yet" };
    }
    return {
      state: "stale", session,
      reason: lastBarMs === null
        ? "session just resumed, but this feed carries no data timestamp — cannot tell a late first bar from an outage"
        : "data stopped before the halt began — the reopen does not explain it",
    };
  }

  return { state: "stale", session, reason: `age ${ageS}s over budget ${budgetS}s during session hours` };
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// BOARD ① — SESSION SEGMENTS. The calendar half of gap compression (R47 ①, spec amended by R49).
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/** A contiguous span of window time, labelled by the CALENDAR — never by whether bars exist. */
export interface SessionSegment {
  from: number;
  to: number;
  /** `true` ⇒ the market was shut for this whole span, on the calendar. */
  closed: boolean;
  /** The phase label at the span's start, for the axis mark. */
  label: string;
}

/**
 * Boundary resolution for the bisect.
 * ⛔ WAS 60_000 ("one minute is the chart's finest rung") AND THAT WAS WRONG BY ITS OWN LOGIC.
 * The bisect reports the first sampled instant PAST the flip, so a 1-minute epsilon puts the
 * boundary up to a minute LATE — and my own arm caught it: the 2026-08-16 reopen resolved to
 * 22:00:07.031Z, classifying the session's first 7 seconds as CLOSED. Sub-bar error, but it
 * lands ON a boundary, which is the one place an axis must not be approximate.
 * ⇒ ***A TOLERANCE CHOSEN FROM THE DATA'S RESOLUTION IS STILL WRONG AT A BOUNDARY.*** One second
 * costs ~6 extra bisect steps per transition and removes the artifact entirely.
 */
const SEGMENT_EPSILON_MS = 1_000;

/**
 * Split `[fromTs, toTs)` into calendar segments, DERIVED FROM `sessionAt` AND NOTHING ELSE.
 *
 * ⛔ WHY IT SAMPLES AND BISECTS RATHER THAN READING THE CALENDAR CONSTANTS DIRECTLY: a second
 * reader of `HALT_START_MIN`/dow rules would be a SECOND CONSTRUCTION of one contract — the
 * two-copies class that produced the pane-B blank (one branch used the shared builder, one
 * hand-wrote it, and they diverged silently). `sessionAt` is the single authority; this walks it.
 * ⇒ ***REUSE BEATS BUILD, AND A CALENDAR COPIED IS A CALENDAR THAT WILL DRIFT.***
 *
 * ⭐ AND THE PROPERTY R49 DEMANDS FALLS OUT BY CONSTRUCTION, NOT BY A SECOND RULE: an UNSCHEDULED
 * hole (an outage inside trading hours — 2026-08-16 22:00–22:23Z exactly) is OPEN calendar time,
 * so it lands in an `closed: false` segment and keeps FULL WIDTH automatically. Nothing has to
 * detect it. A compressor keyed on bar-absence would have had to special-case it, and would have
 * swallowed it the first time someone forgot. **Keying on the calendar is what makes the defect
 * unhideable** — ab and CC-ee both warned of exactly this before a line existed.
 */
export function sessionSegments(fromTs: number, toTs: number): SessionSegment[] {
  if (!Number.isFinite(fromTs) || !Number.isFinite(toTs) || toTs <= fromTs) {
    // Fail LOUD: a degenerate window is a caller defect, and returning [] would draw a blank axis
    // that looks like "no closed spans" rather than "you asked me nothing".
    throw new Error(`sessionSegments: need a finite fromTs < toTs; got ${fromTs}..${toTs}`);
  }
  const closedAt = (t: number): boolean => sessionAt(t).marketClosed;
  const labelAt = (t: number): string => sessionAt(t).label;

  // Coarse walk, then bisect each transition to SEGMENT_EPSILON_MS. Sampling at the epsilon
  // outright would be exact too, but a 4-day window at 1-minute steps is ~5,760 sessionAt calls
  // on every viewport change; this is ~100 plus a bounded bisect per transition.
  const coarse = Math.max(SEGMENT_EPSILON_MS, Math.floor((toTs - fromTs) / 96));
  const segments: SessionSegment[] = [];
  let spanStart = fromTs;
  let spanClosed = closedAt(fromTs);
  let prev = fromTs;

  for (let t = fromTs + coarse; ; t = Math.min(t + coarse, toTs)) {
    const cur = closedAt(t);
    if (cur !== spanClosed) {
      // Bisect (prev, t] for the flip instant.
      let lo = prev;
      let hi = t;
      while (hi - lo > SEGMENT_EPSILON_MS) {
        const mid = lo + Math.floor((hi - lo) / 2);
        if (closedAt(mid) === spanClosed) lo = mid;
        else hi = mid;
      }
      segments.push({ from: spanStart, to: hi, closed: spanClosed, label: labelAt(spanStart) });
      spanStart = hi;
      spanClosed = cur;
    }
    prev = t;
    if (t >= toTs) break;
  }
  segments.push({ from: spanStart, to: toTs, closed: spanClosed, label: labelAt(spanStart) });
  return segments;
}
